import React, { Fragment, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, Download, ArrowUpDown } from 'lucide-react';
import { useIsMobile } from './useIsMobile';

const PAGE = 25;

const toCsv = (columns, rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.label)).join(';');
  const body = rows.map((r) => columns.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(';'));
  return [head, ...body].join('\n');
};

const download = (name, text) => {
  const url = URL.createObjectURL(new Blob(['\ufeff' + text], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
};

const Pager = ({ page, pages, setPage, testId }) => (pages <= 1 ? null : (
  <div className="flex items-center justify-end gap-2 text-xs" data-testid={`${testId}-pager`}>
    <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>Anterior</Button>
    <span className="text-muted-foreground">{page + 1} de {pages}</span>
    <Button size="sm" variant="outline" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>Próxima</Button>
  </div>
));

// Sortable, searchable, paginated table. `expand(row)` renders a detail row; `csvName` enables export.
// `card(row)` (optional) replaces the table with a list of cards on phones.
export const DataTable = ({ columns, rows, testId, searchKey, csvName, csvLabel = 'CSV', expand, initialSort, card, empty = 'Nenhum produto encontrado.' }) => {
  const mobile = useIsMobile();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState({});
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    let r = t && searchKey ? rows.filter((x) => String(x[searchKey] || '').toLowerCase().includes(t)) : rows;
    if (sort) r = [...r].sort((a, b) => ((a[sort.key] ?? -Infinity) > (b[sort.key] ?? -Infinity) ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1));
    return r;
  }, [rows, q, sort, searchKey]);
  const pages = Math.ceil(list.length / PAGE);
  const shown = list.slice(page * PAGE, page * PAGE + PAGE);
  const toggleSort = (k) => { setSort((s) => ({ key: k, dir: s?.key === k && s.dir === 'desc' ? 'asc' : 'desc' })); setPage(0); };
  return (
    <div className="space-y-2" data-testid={testId}>
      {(searchKey || csvName) && (
        <div className="flex flex-wrap items-center gap-2">
          {searchKey && <Input placeholder="Buscar produto…" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} className="h-8 w-full sm:w-64" data-testid={`${testId}-search`} />}
          {csvName && rows.length > 0 && <Button size="sm" variant="outline" className="ml-auto" onClick={() => download(csvName, toCsv(columns, list))} data-testid={`${testId}-csv`}><Download className="h-3.5 w-3.5 mr-1" />{csvLabel}</Button>}
        </div>
      )}
      {card && mobile ? (
        <ul className="space-y-2" data-testid={`${testId}-cards`}>
          {shown.map((r, i) => <li key={r.key || i} className="rounded-md border p-3 text-sm" data-testid={`${testId}-card`}>{card(r)}</li>)}
          {!shown.length && <li className="py-4 text-center text-sm text-muted-foreground">{q ? 'Nenhum produto encontrado.' : empty}</li>}
        </ul>
      ) : (
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              {expand && <th className="w-8" />}
              {columns.map((c) => (
                <th key={c.key} className={`px-3 py-2 font-medium whitespace-nowrap ${c.num ? 'text-right' : 'text-left'}`}>
                  {c.sort === false ? c.label : (
                    <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(c.key)} data-testid={`${testId}-sort-${c.key}`}>
                      {c.label}<ArrowUpDown className="h-3 w-3" />
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const id = r.key || `${page}-${i}`;
              return (
                <Fragment key={id}>
                  <tr className="border-t hover:bg-muted/30" data-testid={`${testId}-row`}>
                    {expand && (
                      <td className="px-2">
                        <button type="button" onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))} aria-label="Ver variações" data-testid={`${testId}-expand`}>
                          {open[id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${c.num ? 'text-right font-mono whitespace-nowrap' : ''} ${c.wrap ? 'min-w-[12rem]' : ''}`}>
                        {c.render ? c.render(r) : c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                      </td>
                    ))}
                  </tr>
                  {expand && open[id] && <tr className="bg-muted/20"><td /><td colSpan={columns.length} className="px-3 py-2">{expand(r)}</td></tr>}
                </Fragment>
              );
            })}
            {!shown.length && <tr><td colSpan={columns.length + (expand ? 1 : 0)} className="px-3 py-6 text-center text-muted-foreground">{q ? 'Nenhum produto encontrado.' : empty}</td></tr>}
          </tbody>
        </table>
      </div>
      )}
      <Pager page={page} pages={pages} setPage={setPage} testId={testId} />
    </div>
  );
};
